// Client-side WebRTC route probe.
//
// WebRTC discovers network paths by gathering ICE candidates, and each candidate carries an IP
// address. That discovery can happen outside a proxy or VPN tunnel (STUN traffic may take the
// direct route), which is the classic WebRTC leak: a site runs this exact probe and reads an
// address your tunnel was supposed to hide.
//
// The probe opens a throwaway RTCPeerConnection against public STUN servers, records every
// candidate as it arrives (so the UI can show the log filling live), classifies each address,
// and reports what a real site would have learned. Everything runs in the visitor's browser;
// nothing here talks to Proxidize servers.

import ipaddr from "ipaddr.js";

export type IpFamily = "ipv4" | "ipv6";

export type CandidateType = "host" | "srflx" | "prflx" | "relay";

/** How an address is classified for the verdict. Only `public` can leak identity. `reserved`
 * covers ranges that cannot identify anyone (multicast, documentation, benchmark blocks), and
 * `unparseable` is anything we could not read as an address at all: a hostname that is not a
 * `.local` mDNS name, or a malformed literal. Unparseable is never treated as protected —
 * evidence we cannot read is not evidence of absence. */
export type AddressKind =
  | "public"
  | "private"
  | "mdns"
  | "loopback"
  | "linkLocal"
  | "cgnat"
  | "reserved"
  | "unparseable";

/** How gathering ended.
 *
 * `complete` — the browser emitted the end-of-candidates sentinel.
 *
 * `settled` — the browser never emitted it, but a STUN server answered and no new candidate
 * arrived for `SETTLE_QUIET_MS`. This is a CONCLUSIVE ending, not a degraded one, and on any
 * machine with a tunnel interface up it is the normal one: Chrome only signals end-of-gathering
 * once every per-interface STUN transaction has timed out, which with several `utun` devices
 * present never happens inside a visitor's patience (measured: still `gathering` at 22s while
 * the srflx answer arrived in 70ms). Gathering-complete is a timeout, not a signal — the W3C
 * WebRTC editors say as much, and advise tracking what has been gathered instead.
 *
 * `timeout` and `failed` runs may simply have been interrupted before the leaking candidate
 * arrived. `errored` means constructing the connection threw: a probing site gets nothing from
 * that attempt, but a throw is not durable evidence of a browser policy (it can be transient),
 * so it is inconclusive rather than a pass. `unsupported` means there is no API at all, which IS
 * conclusive. */
export type ProbeStatus = "complete" | "settled" | "timeout" | "failed" | "errored" | "unsupported";

/**
 * Which endings prove a measurement actually happened, and so may carry a clean verdict.
 *
 * A `Record` rather than a `Set` or an array on purpose: a sixth `ProbeStatus` is a compile error
 * here instead of silently defaulting to "not conclusive" (or worse, to conclusive) in a table
 * nobody remembered to update. `assessLeak` reads it, and the test suites enumerate from it.
 *
 * `unsupported` is false: a run with no API measured nothing. Its pass is issued by the explicit
 * guard in `assessLeak`, which is a statement about the missing API rather than about gathering.
 */
export const STATUS_CONCLUSIVE: Record<ProbeStatus, boolean> = {
  complete: true,
  settled: true,
  timeout: false,
  failed: false,
  errored: false,
  unsupported: false,
};

/** Every `ProbeStatus`, derived so a new one cannot be missed by a hand-maintained list. */
export const PROBE_STATUSES = Object.keys(STATUS_CONCLUSIVE) as ProbeStatus[];

/**
 * Gathering deadlines, sized against the schedule browsers actually retransmit on.
 *
 * Not RFC 8489's illustrative 500ms-RTO example: libwebrtc (what Chrome runs) uses a 250ms
 * initial RTO, doubling, capped at 8000ms, over 9 sends — so real attempts leave at 0, 250, 750,
 * 1750, 3750, 7750, 15750, 23750 and 31750ms, with inter-send gaps of 250, 500, 1000, 2000 and
 * 4000ms. An answer can only first arrive shortly after one of those offsets, which is what makes
 * "it has gone quiet" mean something. Firefox uses nICEr and does not behave this way; it
 * completes gathering promptly, takes the sentinel fast path, and never reaches these constants.
 */
/** Quiet window after the last candidate before a run with an answer is called settled. Covers the
 * first two inter-send gaps (250 and 500ms) plus a slow RTT, so it cannot truncate a burst still
 * in flight. Past that it is a chosen coalescing window, not a derived one: waiting for the 1000ms
 * gap too would cost every visitor a second to catch a case the tail already covers. Invisible on
 * a healthy run, which ends on the sentinel long before this. */
export const SETTLE_QUIET_MS = 700;
/** Ceiling on the quiet window, armed WHEN A SERVER FIRST ANSWERS rather than at the start of the
 * run — see the arming site for why that distinction is load-bearing. So the guarantee is "once
 * any STUN server answers, a verdict lands within 4s of that answer", whatever else the browser
 * is still dribbling. */
export const SETTLE_CEILING_MS = 4000;
/** How long we keep listening AFTER publishing a settled verdict. Covers the send at 7750ms plus
 * RTT: that is the latest a lossy-but-working interface can realistically return a srflx, and if
 * one does, the verdict is revised rather than left wrong. Deliberately not out to 15750ms — a
 * path that lost five consecutive packets is not a working path, and the report discloses the
 * window. */
export const LATE_WATCH_MS = 10_000;
/** Hard cap. Only reachable when no STUN server ever answered, since any answer settles first. */
export const PROBE_TIMEOUT_MS = 20_000;
/** When to tell the caller "this is taking a while" so it can say so instead of showing nothing. */
export const PROBE_SLOW_AFTER_MS = 3000;

/** A candidate only a server could have produced, and so proof that one answered. One predicate,
 * because the probe and the UI must not disagree about what counts as an answer. */
export function isServerDerived(candidate: IceCandidateRecord): boolean {
  return candidate.type === "srflx" || candidate.type === "relay";
}

/** One ICE candidate exactly as the browser emitted it, timestamped for the live log. */
export interface IceCandidateRecord {
  /** Milliseconds since the probe started. */
  atMs: number;
  type: CandidateType | "unknown";
  protocol: "udp" | "tcp" | "unknown";
  address: string;
  port: number | null;
  /** For reflexive candidates, the local address the mapping was derived from (raddr). */
  relatedAddress: string | null;
  raw: string;
}

/** One distinct address across all candidates that carried it. */
export interface DiscoveredAddress {
  address: string;
  family: IpFamily;
  kind: AddressKind;
  /** Candidate types that surfaced this address (a srflx hit is the classic STUN leak path). */
  sources: CandidateType[];
  firstSeenMs: number;
}

export interface WebRtcProbeResult {
  supported: boolean;
  /**
   * Whether the browser emitted the end-of-candidates sentinel.
   *
   * Named for the event rather than for an outcome: as `complete` it read as a rival to
   * `measurementComplete` and invited the reading that a `settled` run was somehow unfinished,
   * when a settled run is conclusive and simply never got the sentinel. Ask
   * `STATUS_CONCLUSIVE[status]` whether a verdict may be trusted; ask this only what the browser
   * did. Kept in the exported JSON because an auditor should be able to tell the two apart.
   */
  endOfCandidates: boolean;
  /** How the run ended. */
  status: ProbeStatus;
  /**
   * True once a srflx or relay candidate arrived: proof a STUN server actually answered, and so
   * that the public-address question was really asked. Surfaced (rather than left closure-local)
   * because a `settled` ending means nothing without it, and `stunUnreachable` only carries the
   * fact inverted and lossily — false there means "answered" OR "nothing arrived at all".
   */
  serverAnswered: boolean;
  durationMs: number;
  candidates: IceCandidateRecord[];
  /** Deduplicated addresses, in first-seen order. */
  addresses: DiscoveredAddress[];
  stunServers: string[];
  /**
   * True when the probe never got an answer out of any STUN server: at least one candidate
   * error arrived and no server-derived candidate ever did.
   *
   * This is load-bearing. A network that cannot resolve the STUN hostnames still finishes
   * gathering — measured: three non-resolving hosts produce error 701 apiece, one mDNS
   * candidate, and end-of-candidates inside a second. Without this flag that run is
   * indistinguishable from a genuinely clean one, and the tool would report a pass having
   * never once asked what its public address looks like.
   */
  stunUnreachable: boolean;
  /** Distinct STUN URLs that reported an error, for the exported report. */
  stunErrors: string[];
}

// Several STUN servers so one blocked or unreachable host can't turn a real leak into a false
// "no public address". The browser races them; the first server-reflexive answer is enough.
export const STUN_SERVERS = [
  "stun:stun.l.google.com:19302",
  "stun:stun1.l.google.com:19302",
  "stun:stun.cloudflare.com:3478",
];

export function familyOf(address: string): IpFamily {
  return address.includes(":") ? "ipv6" : "ipv4";
}

type ParsedIp = ReturnType<typeof ipaddr.parse>;

/**
 * Parse one address with a standards-compliant parser, unwrapping IPv4-mapped IPv6 to the IPv4
 * it names. Returns null for anything that is not an IP literal — a `.local` mDNS name, a
 * hostname, or a malformed value. Nothing downstream is allowed to guess at a null.
 */
function parseIp(raw: string): ParsedIp | null {
  const zone = raw.indexOf("%"); // "fe80::1%eth0" → "fe80::1"
  const text = (zone === -1 ? raw : raw.slice(0, zone)).trim();
  try {
    const parsed = ipaddr.parse(text);
    // Every spelling of a mapped address ("::ffff:1.2.3.4", "0:0:0:0:0:ffff:1.2.3.4",
    // "::ffff:0102:0304") collapses to the same IPv4, so the family and the comparison key
    // agree no matter which form the browser or the proxy header used.
    return parsed instanceof ipaddr.IPv6 && parsed.isIPv4MappedAddress()
      ? parsed.toIPv4Address()
      : parsed;
  } catch {
    return null;
  }
}

/**
 * Globally reachable assignments that sit INSIDE one of the library's broad special ranges.
 * Checked before the range table, because the range table is coarse: ipaddr.js reports the whole
 * of 192.0.0.0/24 and 2001::/23 as `reserved`, which would otherwise suppress these — and
 * suppressing a reachable address hides a leak, the one error class this tool must not make.
 * Every entry is marked "Globally Reachable: True" in the IANA special-purpose registries.
 */
const PUBLIC_OVERRIDES = [
  "192.0.0.9/32", // Port Control Protocol anycast
  "192.0.0.10/32", // Traversal Using Relays around NAT anycast
  "2001:1::1/128", // Port Control Protocol anycast
  "2001:1::2/128", // TURN anycast
  "2001:1::3/128", // DNS-SD service registration discovery
].map((cidr) => ipaddr.parseCIDR(cidr));

/**
 * Non-reachable prefixes the installed ipaddr.js does not know, or knows too coarsely.
 * `64:ff9b:1::/48` is local-use NAT64 (RFC 8215) sharing the library's `rfc6052` name with the
 * globally reachable /96, and `100:0:0:1::/64` is the 2025 Dummy prefix, absent from the library.
 */
const EXTRA_HARMLESS: [ReturnType<typeof ipaddr.parseCIDR>, AddressKind][] = [
  [ipaddr.parseCIDR("64:ff9b:1::/48"), "reserved"],
  [ipaddr.parseCIDR("100:0:0:1::/64"), "reserved"],
];

/**
 * The ranges that cannot carry a globally reachable address, keyed by ipaddr.js range name.
 * Anything NOT listed here is treated as `public`, because mistaking a real public address for a
 * harmless one would hide a leak — the worse of the two errors. That deliberately leaves the
 * ranges IANA marks globally reachable alone: 6to4, Teredo and NAT64 (`rfc6052`/`rfc6145`) can
 * be routable and can encode a public IPv4, AS112 and AMT anycast are reachable, and ORCHIDv2
 * stays out as well.
 *
 * Keys are checked against the library's own range names by a test, because this is a plain
 * string map: a misspelled key would silently promote its range to `public` with nothing in the
 * type system to catch it. `deprecatedOrchid` is exactly that — it is not `deprecated`.
 */
export const HARMLESS_RANGES: Record<string, AddressKind> = {
  private: "private",
  uniqueLocal: "private",
  deprecatedSiteLocal: "private", // fec0::/10, the withdrawn site-local range
  loopback: "loopback",
  linkLocal: "linkLocal",
  carrierGradeNat: "cgnat",
  unspecified: "reserved",
  broadcast: "reserved",
  multicast: "reserved",
  reserved: "reserved",
  discard: "reserved",
  segmentRouting: "reserved",
  benchmarking: "reserved",
  deprecatedOrchid: "reserved", // 2001:10::/28, not globally reachable
  // Deliberately absent: droneRemoteIdProtocolEntityTags (2001:30::/28) IS globally reachable.
};

/**
 * One address, one spelling. The candidate comes from an SDP line and the baseline from an HTTP
 * header, two producers that may spell the same address differently (case, IPv6 zero
 * compression, zone ids, IPv4-mapped IPv6), so every comparison goes through this first.
 * A value that is not an IP literal comes back trimmed and lowercased, and simply compares as
 * itself — two `.local` names still match each other and nothing else.
 */
export function canonicalizeIp(raw: string): string {
  return parseIp(raw)?.toNormalizedString() ?? raw.trim().toLowerCase();
}

/** Classify an address the way the verdict needs it. Range data comes from ipaddr.js, which
 * tracks the IANA special-purpose registries, rather than from a hand-written prefix table. */
export function classifyAddress(address: string): AddressKind {
  const text = address.trim().toLowerCase();
  if (text.endsWith(".local")) return "mdns";
  const parsed = parseIp(address);
  if (!parsed) return "unparseable";
  // Order matters: a reachable exception inside a broad reserved range must win over the range.
  for (const cidr of PUBLIC_OVERRIDES) {
    if (parsed.kind() === cidr[0].kind() && parsed.match(cidr)) return "public";
  }
  for (const [cidr, kind] of EXTRA_HARMLESS) {
    if (parsed.kind() === cidr[0].kind() && parsed.match(cidr)) return kind;
  }
  return HARMLESS_RANGES[parsed.range()] ?? "public";
}

/** candidate line: <foundation> <component> <proto> <priority> <address> <port> typ <type> ... */
function parseCandidate(raw: string, atMs: number): IceCandidateRecord | null {
  const parts = raw.split(" ");
  if (parts.length < 8) return null;
  const address = parts[4];
  if (!address) return null;
  const port = Number(parts[5]);
  const typeMatch = /typ (\w+)/.exec(raw);
  const type = typeMatch?.[1];
  const raddr = /raddr ([^\s]+)/.exec(raw)?.[1] ?? null;
  const proto = parts[2]?.toLowerCase();
  return {
    atMs,
    type:
      type === "host" || type === "srflx" || type === "prflx" || type === "relay"
        ? type
        : "unknown",
    protocol: proto === "udp" || proto === "tcp" ? proto : "unknown",
    address,
    port: Number.isFinite(port) ? port : null,
    // A raddr of 0.0.0.0 is a placeholder the browser uses to hide the base address, not data.
    relatedAddress: raddr && raddr !== "0.0.0.0" ? raddr : null,
    raw,
  };
}

interface ProbeOptions {
  timeoutMs?: number;
  settleQuietMs?: number;
  settleCeilingMs?: number;
  lateWatchMs?: number;
  slowAfterMs?: number;
  /** Fired once per candidate as the browser emits it, for the live log. */
  onCandidate?: (candidate: IceCandidateRecord) => void;
  /**
   * Fired once if there is still no verdict at `slowAfterMs`, so the caller can say "still
   * looking" instead of showing nothing.
   *
   * Takes NO arguments on purpose. Its predecessor handed over a full result snapshot, which the
   * caller then published — and because that snapshot was stamped `timeout`, the UI asserted "the
   * test couldn't finish" three seconds into a run that was still going. A callback that cannot
   * carry a result cannot be mistaken for one.
   */
  onSlow?: () => void;
  /**
   * Fired if a candidate arrives AFTER a settled verdict was published, coalesced through the
   * same quiet window so a late burst is one call. The caller re-publishes; that revision is what
   * makes the early answer safe rather than a guess.
   */
  onLate?: (snapshot: WebRtcProbeResult) => void;
  /** Tear the connection down early — a re-run, or the visitor leaving the page. The promise still
   * resolves (this function never rejects) with whatever had been gathered by then. */
  signal?: AbortSignal;
}

/**
 * Run the ICE gathering probe. The promise never rejects, and the peer connection is always
 * closed.
 *
 * It resolves on the first of these to happen:
 *
 * 1. the browser emits the end-of-candidates sentinel → `complete`;
 * 2. a STUN server has answered and no candidate has arrived for `settleQuietMs` (or
 *    `settleCeilingMs` has passed since the run began) → `settled`;
 * 3. `timeoutMs` → `timeout`, only reachable when no server ever answered.
 *
 * Rule 2 exists because rule 1 is not a signal a tool can wait for. Chrome emits the sentinel
 * only once every per-interface STUN transaction has timed out, so one unreachable tunnel device
 * — a VPN, WireGuard, Tailscale — pushes it past any cap worth showing a visitor, while the
 * answer itself has been sitting there since 70ms. Waiting on it converted the most common
 * proxy-user setup into "the test couldn't finish".
 *
 * Rule 2 is safe because of what happens after it. STUN retransmits on RFC 8489's schedule (0,
 * 0.5, 1.5, 3.5, 7.5s), so a lossy-but-working interface can still answer seconds after the
 * quiet window closed. So the connection stays OPEN for `lateWatchMs` past a settled resolve, and
 * anything that arrives in that tail is handed to `onLate` for the caller to revise its verdict
 * with. The early answer is not a shortcut past the tail; it is the tail moved off the critical
 * path.
 */
export function runWebRtcProbe(options: ProbeOptions = {}): Promise<WebRtcProbeResult> {
  const {
    timeoutMs = PROBE_TIMEOUT_MS,
    settleQuietMs = SETTLE_QUIET_MS,
    settleCeilingMs = SETTLE_CEILING_MS,
    lateWatchMs = LATE_WATCH_MS,
    slowAfterMs = PROBE_SLOW_AFTER_MS,
    onCandidate,
    onSlow,
    onLate,
    signal,
  } = options;
  const RTC = typeof window !== "undefined" ? window.RTCPeerConnection : undefined;

  if (!RTC) {
    return Promise.resolve({
      supported: false,
      endOfCandidates: false,
      status: "unsupported",
      serverAnswered: false,
      durationMs: 0,
      candidates: [],
      addresses: [],
      stunServers: STUN_SERVERS,
      stunUnreachable: false,
      stunErrors: [],
    });
  }

  return new Promise<WebRtcProbeResult>((resolve) => {
    const startedAt = performance.now();
    const candidates: IceCandidateRecord[] = [];
    const byAddress = new Map<string, DiscoveredAddress>();
    const stunErrors = new Set<string>();
    // A srflx or relay candidate is proof a server actually answered.
    let serverAnswered = false;
    // Two latches, not one. `resolved` means the caller has its verdict; `closed` means we have
    // stopped listening. A settled run sits between them for `lateWatchMs`, which is the whole
    // mechanism that lets the answer be both early and revisable.
    let resolved = false;
    let closed = false;
    let settledStatus: ProbeStatus | null = null;
    let pc: RTCPeerConnection;

    const snapshot = (status: ProbeStatus): WebRtcProbeResult => ({
      supported: true,
      endOfCandidates: status === "complete",
      status,
      serverAnswered,
      durationMs: Math.round(performance.now() - startedAt),
      candidates: [...candidates],
      addresses: [...byAddress.values()],
      stunServers: STUN_SERVERS,
      // "Candidates arrived, but none of them came from a server."
      //
      // The error event alone is not enough to detect this: `icecandidateerror` only landed in
      // Firefox 150, so on Firefox ESR and every older build no error ever fires and a blocked
      // STUN path used to report a clean pass — the exact false all-clear this verdict exists to
      // prevent, on the browser family most over-represented among privacy-tool visitors.
      //
      // A completed run that produced candidates but no srflx and no relay has not tested the
      // STUN path, whatever the error surface says. A run with NO candidates at all is a
      // different fact and keeps its own `silent` verdict, which sits ahead of this in the ladder.
      stunUnreachable: !serverAnswered && (stunErrors.size > 0 || candidates.length > 0),
      stunErrors: [...stunErrors],
    });

    try {
      pc = new RTC({ iceServers: [{ urls: STUN_SERVERS }] });
    } catch {
      // Construction throwing means WebRTC is present but unusable. A probing site gets nothing
      // from this attempt either — but a throw can be transient (resource exhaustion, a
      // half-installed extension), and one failed construction is not proof of a durable
      // browser policy. So this is inconclusive, never a pass.
      resolve(snapshot("errored"));
      return;
    }

    // Mutable because they are re-armed: the quiet window restarts on every candidate, the
    // ceiling is armed only once a server has actually answered, and the tail timer exists only
    // after a verdict has been published. The two one-shot timers stay `const` below, declared
    // after the closures that reference them — safe because none of those runs before then.
    let quietTimer: ReturnType<typeof setTimeout> | undefined;
    let ceilingTimer: ReturnType<typeof setTimeout> | undefined;
    let lateTimer: ReturnType<typeof setTimeout> | undefined;
    /** Something arrived after the verdict was published, so the tail owes the caller a delivery. */
    let lateDirty = false;

    const stopListening = () => {
      if (closed) return;
      closed = true;
      clearTimeout(hardTimer);
      clearTimeout(slowTimer);
      clearTimeout(quietTimer);
      clearTimeout(ceilingTimer);
      clearTimeout(lateTimer);
      try {
        pc.close();
      } catch {
        /* already closed */
      }
    };

    /**
     * End the tail, handing over anything it turned up — exactly once, and only if there is
     * something to hand over.
     *
     * This used to be a coalesced stream: each late burst re-armed the quiet window and delivered
     * when it expired. That silently dropped the very thing the tail exists for. A leak arriving
     * while other candidates were still trickling in faster than the quiet window never got
     * delivered at all, because the window kept being pushed back until the tail closed underneath
     * it — so the run kept its clean verdict and the caller was never told. One guaranteed
     * delivery at the end cannot be outrun that way.
     */
    const flushLate = (status: ProbeStatus) => {
      if (closed) return;
      const pending = lateDirty ? snapshot(status) : null;
      lateDirty = false;
      stopListening();
      if (pending) onLate?.(pending);
    };

    /** Terminal: publish and stop. */
    const finish = (status: "complete" | "timeout" | "failed") => {
      if (resolved) {
        // The browser finally signalled the end while we were watching the tail. Close early and
        // deliver whatever the tail collected, stamped with the ending we actually got.
        if (status === "complete") flushLate("complete");
        else stopListening();
        return;
      }
      resolved = true;
      stopListening();
      resolve(snapshot(status));
    };

    /** Publish a verdict but keep listening, so a slow interface can still be caught. */
    const settle = () => {
      if (resolved || closed || !serverAnswered) return;
      resolved = true;
      settledStatus = "settled";
      clearTimeout(hardTimer);
      clearTimeout(slowTimer);
      clearTimeout(quietTimer);
      clearTimeout(ceilingTimer);
      lateTimer = setTimeout(() => flushLate(settledStatus ?? "settled"), lateWatchMs);
      resolve(snapshot("settled"));
    };

    // Re-armed by every candidate event before a verdict exists: "nothing new for a while" is the
    // only evidence we get that the browser has stopped, since it will not say so itself.
    const armQuiet = () => {
      if (closed || resolved) return;
      clearTimeout(quietTimer);
      quietTimer = setTimeout(settle, settleQuietMs);
    };

    // Abandoning mid-run is not a failure of the browser's, so it reports what it had rather than
    // inventing an ending: an already-resolved run just stops listening.
    signal?.addEventListener("abort", () => finish("timeout"), { once: true });

    const hardTimer = setTimeout(() => finish("timeout"), timeoutMs);
    const slowTimer = setTimeout(() => {
      if (!resolved) onSlow?.();
    }, slowAfterMs);

    // Every failed STUN URL is recorded. Without this the tool cannot tell "your public address
    // was never revealed" from "we never managed to ask".
    pc.onicecandidateerror = (event) => {
      if (closed) return;
      const { url, errorCode } = event as RTCPeerConnectionIceErrorEvent;
      stunErrors.add(url || `error ${errorCode}`);
      // Deliberately does NOT re-arm the quiet window. These errors ARE the per-interface STUN
      // timeouts, and they land at 10s+ — resetting on them would push every tunnel user's
      // verdict back out to the cap, which is the bug this design exists to remove. An error also
      // cannot change a verdict once a server has answered: `stunUnreachable` is already false.
    };

    pc.onicecandidate = (event) => {
      if (closed) return;
      if (!event.candidate || !event.candidate.candidate) {
        finish("complete"); // null candidate signals gathering complete
        return;
      }
      // Before the parse guard below: an unreadable candidate line is still evidence the browser
      // is working, so it should delay settling rather than be ignored. Before `onCandidate` too,
      // so a throwing callback cannot leave the window un-armed.
      //
      // After a verdict exists the quiet window is not re-armed at all — the tail owes exactly one
      // delivery at its end, and re-arming here is what used to let a steady trickle push that
      // delivery past the close and swallow a leak.
      if (resolved) lateDirty = true;
      else armQuiet();
      const record = parseCandidate(
        event.candidate.candidate,
        Math.round(performance.now() - startedAt)
      );
      if (!record) return;
      candidates.push(record);
      if (isServerDerived(record) && !serverAnswered) {
        serverAnswered = true;
        // The ceiling is armed HERE, not at the start of the run. Armed from the start it was a
        // one-shot no-op whenever the answer landed after it — Chrome's fourth STUN send is at
        // 3750ms, so one lost packet put the answer past a 4s ceiling, and a run still trickling
        // candidates then had nothing left to end it before the 20s cap. Which was the original
        // bug, one dropped packet away. Armed from the answer, the guarantee the constant claims
        // is the one it actually provides.
        clearTimeout(ceilingTimer);
        ceilingTimer = setTimeout(settle, settleCeilingMs);
      }

      // Fold the candidate's addresses into the deduplicated set. A reflexive candidate's
      // related address is a discovery too: it names the local base the mapping came from.
      const seen: { address: string; source: CandidateType }[] = [];
      if (record.type !== "unknown") {
        seen.push({ address: record.address, source: record.type });
        if (record.relatedAddress) seen.push({ address: record.relatedAddress, source: "host" });
      }
      for (const { address, source } of seen) {
        // Keyed by canonical form: two spellings of one address are ONE discovery. Keyed by the
        // raw string they became two, and the second lost its network evidence downstream.
        const key = canonicalizeIp(address);
        const existing = byAddress.get(key);
        if (existing) {
          if (!existing.sources.includes(source)) existing.sources.push(source);
        } else {
          byAddress.set(key, {
            address, // the first spelling seen, kept for display
            family: familyOf(key),
            kind: classifyAddress(address),
            sources: [source],
            firstSeenMs: record.atMs,
          });
        }
      }
      onCandidate?.(record);
    };

    // A synchronous throw here would reject the promise this function promises never to
    // reject, leaving the caller stuck on the scanning screen — hence the try around it.
    try {
      pc.createDataChannel("route-probe");
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .catch(() => finish("failed"));
    } catch {
      finish("failed");
    }
  });
}

// --- Assessment ------------------------------------------------------------------------------
//
// Pure comparison of what the probe surfaced against what the visitor's connection already
// shows. Kept out of the component so the verdict the panel renders and the verdict the exports
// describe come from one function and cannot drift.

/** What we know about one public address WebRTC surfaced, after comparing it. */
export interface ComparedAddress {
  address: string;
  family: IpFamily;
  sources: CandidateType[];
  /** Equals the connection's exit address, after canonicalising both spellings. */
  matchesConnection: boolean;
  /** Same autonomous system as the connection (only when both ASNs resolved). */
  sameNetwork: boolean | null;
  /** The partition the verdict is built from, precomputed here so the per-address labels the
   * UI shows can never disagree with the headline. */
  disposition: "matches" | "leaked" | "second-address" | "unverified";
}

export type LeakVerdictKind =
  /** WebRTC surfaced a public address the connection does not use: either it is on a different
   * network, or it is in the SAME address family as the connection — a same-family extra can
   * never be the dual-stack pair, so an equal ASN doesn't soften it. Note what this does and
   * does not prove: an address the HTTP connection isn't using. Whether that address is the
   * visitor's own ISP line or simply a second proxy egress is an inference the tool cannot
   * make, so the copy must not assert "your real IP". */
  | "leak"
  /** Extra public address in the OTHER address family, resolving to the connection's own
   * network: the dual-stack pattern (IPv6 running next to IPv4). Consistent with dual stack,
   * but an equal ASN cannot rule out a bypass through the same operator. */
  | "second-address"
  /** Extra public address whose network lookup failed, so the dual-stack explanation could not
   * be checked. Presented with suspicion, never as safe. */
  | "extra-unverified"
  /** Public address(es) surfaced but the connection baseline was unreachable, so no comparison. */
  | "exposed-unverified"
  /** The connection's own exit address changed between the start and the end of the run (a
   * rotating proxy or gateway), so a mismatch cannot be told apart from a bypass. */
  | "route-changed"
  /** A mismatch surfaced, but re-reading the connection's exit address failed, so route
   * stability is unknown and a rotating proxy cannot be ruled out. "We could not check" must
   * not read as an accusation any more than it reads as safety. */
  | "route-unverified"
  /** No STUN server could be reached, so public-address discovery never actually ran. Note that
   * gathering still reports itself COMPLETE in this case: the browser finishes tidily having
   * asked nobody anything, which is precisely why this needs its own verdict. */
  | "stun-unreachable"
  /** Gathering completed and every public address matches the connection's exit address. */
  | "match"
  /** Gathering completed; candidates arrived but none carried a public address, and nothing
   * local or unreadable surfaced either. */
  | "no-public"
  /** Gathering completed and the only findings were a raw private/link-local address: not
   * routable, but handed to any script, so this is not a clean pass. */
  | "local-exposed"
  /** Gathering completed and the only findings were CGNAT addresses (`100.64.0.0/10`). Its own
   * verdict rather than a case of `local-exposed`, because that range is a mobile operator's
   * shared NAT space (and Tailscale's), not the visitor's own network — see `cgnatExposed`. */
  | "cgnat-exposed"
  /** Gathering completed but something surfaced that we could not read as an address at all.
   * Unreadable evidence is not absence of evidence, so this cannot be a pass. */
  | "unreadable"
  /** Gathering timed out or errored before the browser finished, and nothing conclusive was
   * seen — more candidates could have followed, so no clean verdict can be claimed. This also
   * covers a peer connection that threw on construction, which is not durable proof of a
   * browser policy. */
  | "incomplete"
  /** Gathering COMPLETED with zero candidates (an extension or policy blocks it quietly). */
  | "silent"
  /** RTCPeerConnection is not available in this browser. The only conclusive negative: with no
   * API there is nothing for a probing site to call. */
  | "unsupported";

export interface LeakAssessment {
  kind: LeakVerdictKind;
  compared: ComparedAddress[];
  /** The subset that neither matches the connection nor has the dual-stack explanation. */
  leaked: ComparedAddress[];
  /** Extras with the dual-stack explanation confirmed (other family, same network). */
  secondAddresses: ComparedAddress[];
  /** Extras whose network lookup failed, so they could not be judged either way. */
  unverified: ComparedAddress[];
  /** Raw private/link-local addresses handed out without mDNS masking: the visitor's own LAN. */
  localExposed: string[];
  /**
   * CGNAT addresses (`100.64.0.0/10`), kept separate from `localExposed` because they are not the
   * visitor's local network and must never be described as one. That range is CGNAT space:
   * it belongs to a mobile operator, and it is also Tailscale's entire range. Telling a 4G-proxy
   * or Tailscale visitor that their "home network address" leaked and pointing them at their
   * browser settings would be wrong on both counts, and those visitors are precisely this tool's
   * audience. Still an exposure, still not a pass, just a different claim.
   */
  cgnatExposed: string[];
  /** Values we could not read as an address at all. */
  unreadable: string[];
  /** Whether gathering finished. Tracked as its own dimension so an exposure verdict can still
   * disclose that later candidates may have been missed, instead of the fact being lost behind
   * a non-clean headline. */
  measurementComplete: boolean;
}

interface ConnectionBaseline {
  ip: string;
  asn?: string;
  /**
   * The visitor's exit address in the OTHER address family, when a family-forced endpoint
   * answered. This is what turns the dual-stack question from an inference into a measurement:
   * a cross-family extra address can be compared for exact equality against the visitor's own
   * exit in that family, instead of guessing from a shared AS number. With this present the
   * verdict needs no network-lookup data at all.
   */
  otherFamily?: { ip: string; asn?: string } | null;
}

export function assessLeak(
  probe: WebRtcProbeResult,
  connection: ConnectionBaseline | null,
  /** ASN per surfaced public address, keyed by CANONICAL address. */
  asnByAddress: Record<string, string | undefined>,
  /**
   * The result of re-reading the connection's exit address AFTER gathering. `attempted` and `ip`
   * are separate on purpose: a bare null conflated "no recheck was needed" with "the recheck
   * failed", and the second of those was silently treated as proof the route held still, turning
   * an unknown into a red leak.
   */
  recheck?: { attempted: boolean; ip: string | null }
): LeakAssessment {
  // Gated on evidence, not on how the run exited. `settled` counts because a server answered and
  // the browser then went quiet; `complete` counts because the browser said so. The second clause
  // is not redundant even though the probe cannot emit one without the other: `applyDemo` and the
  // test factories build these objects by hand, and this module's rule is that unreadable evidence
  // is never treated as absence.
  const measurementComplete =
    STATUS_CONCLUSIVE[probe.status] && (probe.status !== "settled" || probe.serverAnswered);
  const localExposed = probe.addresses
    .filter((a) => a.kind === "private" || a.kind === "linkLocal")
    .map((a) => a.address);
  const cgnatExposed = probe.addresses.filter((a) => a.kind === "cgnat").map((a) => a.address);
  const unreadable = probe.addresses.filter((a) => a.kind === "unparseable").map((a) => a.address);
  const empty = {
    compared: [],
    leaked: [],
    secondAddresses: [],
    unverified: [],
    localExposed,
    cgnatExposed,
    unreadable,
    measurementComplete,
  };
  // No API at all is the one conclusive negative: there is nothing for a site to call. A
  // constructor throw is NOT in this branch — it may be transient, so it stays inconclusive.
  //
  // Guarded on there being nothing to report, so the claim can never contradict the evidence
  // alongside it. A probe with no API cannot have surfaced an address, so this costs nothing in
  // practice; it only stops a malformed result from being labelled green while carrying findings.
  if (probe.status === "unsupported" && probe.addresses.length === 0) {
    return { kind: "unsupported", ...empty };
  }

  const baselineIp = connection ? canonicalizeIp(connection.ip) : null;
  // Family is read off the CANONICAL form: an IPv4-mapped baseline ("::ffff:1.2.3.4") names an
  // IPv4 address, and calling it IPv6 would make every IPv4 extra look cross-family and so
  // wrongly qualify for the dual-stack downgrade.
  const baselineFamily = baselineIp ? familyOf(baselineIp) : null;
  // Only counted when it really is the other family: a misconfigured endpoint answering with the
  // same family as the primary baseline must not be used to settle a cross-family comparison.
  //
  // Validated as a real public address before it is trusted, not merely shape-checked. This
  // baseline comes from a separately hosted endpoint, and once accepted it decides the verdict
  // outright — so a misconfigured or stale endpoint answering `{"ip":"unknown"}` would otherwise
  // pass the family test (no colon reads as IPv4) and turn every dual-stack visitor red.
  const otherFamilyRaw = connection?.otherFamily?.ip
    ? canonicalizeIp(connection.otherFamily.ip)
    : null;
  const otherFamilyIp =
    otherFamilyRaw &&
    baselineFamily &&
    classifyAddress(otherFamilyRaw) === "public" &&
    familyOf(otherFamilyRaw) !== baselineFamily
      ? otherFamilyRaw
      : null;

  const compared: ComparedAddress[] = probe.addresses
    .filter((a) => a.kind === "public")
    .map((a) => {
      const canonical = canonicalizeIp(a.address);
      const family = familyOf(canonical);
      const matchesConnection = baselineIp !== null && canonical === baselineIp;
      const ownAsn = asnByAddress[canonical];
      // When the visitor's exit in the other family is known, a cross-family extra is settled by
      // exact equality — measured, not inferred. `sameNetwork` then only records whether the two
      // sit on one network, for the report; it no longer decides anything.
      const matchesOtherFamily =
        otherFamilyIp !== null && family !== baselineFamily && canonical === otherFamilyIp;
      const sameNetwork =
        matchesConnection ||
        matchesOtherFamily ||
        (ownAsn && connection?.asn ? ownAsn === connection.asn : null);
      // The dual-stack explanation is only ever available to an address in the OTHER family: a
      // same-family extra cannot be the pair, so it stays a leak however the networks compare.
      // Cross-family is settled by the other-family baseline when we have it, and falls back to
      // ASN when we don't. An unresolved fallback stays unverified, because "we couldn't check"
      // must never read as safe.
      const disposition: ComparedAddress["disposition"] = matchesConnection
        ? "matches"
        : baselineIp === null
          ? "unverified"
          : family === baselineFamily
            ? "leaked"
            : otherFamilyIp !== null
              ? matchesOtherFamily
                ? "second-address" // proven: it is the visitor's own exit in that family
                : // A third address, neither of the visitor's two exits. Deliberately NOT softened
                  // by a matching ASN: a shared operator is exactly the signal that let a
                  // same-operator mobile proxy hide a real bypass, so it cannot be trusted to
                  // rescue an address here either. Direct evidence that this is not their exit
                  // outranks a hint about who announces it. The endpoint is validated above, which
                  // is where a misconfigured answer gets caught.
                  "leaked"
              : sameNetwork === false
                ? "leaked"
                : sameNetwork === true
                  ? "second-address"
                  : "unverified";
      return {
        address: a.address,
        family,
        sources: a.sources,
        matchesConnection,
        sameNetwork,
        disposition,
      };
    });

  const leaked = compared.filter((c) => c.disposition === "leaked");
  const secondAddresses = compared.filter((c) => c.disposition === "second-address");
  const unverified = compared.filter((c) => c.disposition === "unverified");
  const partition = {
    compared,
    leaked,
    secondAddresses,
    unverified,
    localExposed,
    cgnatExposed,
    unreadable,
  };

  if (connection === null && compared.length > 0) {
    return { kind: "exposed-unverified", ...partition, measurementComplete };
  }

  // A mismatch is only meaningful if the thing being compared against held still. Three states,
  // not two: the route rotated, the route held, or we could not find out — and the last of those
  // must not be reported as either a leak or a pass.
  const hasMismatch = leaked.length > 0 || unverified.length > 0 || secondAddresses.length > 0;
  const routeChanged =
    baselineIp !== null &&
    typeof recheck?.ip === "string" &&
    canonicalizeIp(recheck.ip) !== baselineIp;
  const routeUnknown = recheck?.attempted === true && recheck.ip === null;

  // Exposure evidence stands even when gathering was cut short, but a CLEAN verdict demands a
  // completed run: an interrupted probe may simply not have reached the leaking candidate yet.
  // Unreadable and raw-local findings block a pass too — neither is proof of nothing. And a run
  // that never reached a STUN server never asked the question at all, however tidily it ended.
  const kind: LeakVerdictKind =
    routeChanged && hasMismatch
      ? "route-changed"
      : routeUnknown && hasMismatch
        ? "route-unverified"
        : leaked.length > 0
          ? "leak"
          : unverified.length > 0
            ? "extra-unverified"
            : secondAddresses.length > 0
              ? "second-address"
              : // Deliberately NOT conditional on whether an address surfaced. A public HOST
                // candidate can appear on an un-NATted machine and happen to equal the
                // baseline, which used to produce a green "match" even though no STUN server
                // ever answered — a pass claimed without the question being asked. If STUN was
                // unreachable the run is inconclusive regardless of what else it found.
                probe.stunUnreachable
                ? "stun-unreachable"
                : unreadable.length > 0
                  ? "unreadable"
                  : localExposed.length > 0
                    ? "local-exposed"
                    : // Ranked below local exposure, so a run carrying both makes the stronger
                      // claim: an actual LAN address says more about the visitor than a shared
                      // CGNAT address does.
                      cgnatExposed.length > 0
                      ? "cgnat-exposed"
                      : // Below every FINDING, above every claim of absence. The rule at the top
                        // of this block — exposure evidence survives an interrupted run — was
                        // being applied only to the public-address rungs, so a cut-short run that
                        // found a raw LAN address threw that finding away and said "the test
                        // couldn't finish" instead. Incompleteness is its own dimension, carried
                        // to the UI's caveat strip by `measurementComplete`, so it does not need
                        // to consume the headline as well.
                        !measurementComplete
                        ? "incomplete"
                        : probe.candidates.length === 0
                          ? "silent"
                          : compared.length === 0
                            ? "no-public"
                            : "match";
  return { kind, ...partition, measurementComplete };
}

/** Which explanation, if any, the local-network reading has earned. Null means we have nothing
 * honest to say, which the UI renders as silence. */
export type LocalNetworkNote =
  | "masked-unprompted"
  | "exposed-with-capture"
  | "exposed-otherwise"
  | null;

/** Only these two kinds are covered by the mDNS masking claim, and this is deliberately NOT the
 * `rawLocal` set the footer line renders. `loopback` was never masked — mDNS obfuscation has
 * never applied to 127.0.0.1, so blaming a browser flag for it would accuse a browser that did
 * nothing wrong. `cgnat` (100.64.0.0/10) is CGNAT: mobile operator space, and
 * Tailscale's whole range. Calling that a visitor's own network and pinning it on their browser
 * settings would be wrong twice over, to precisely the mobile-proxy users this tool is for. */
function maskableLocals(addresses: readonly { kind: AddressKind }[]): number {
  return addresses.filter((a) => a.kind === "private" || a.kind === "linkLocal").length;
}

/**
 * Explains the local-network reading using the origin's capture permission.
 *
 * Takes the permission as an inline union rather than importing `CapturePermission`, so this
 * module keeps its zero-dependency shape.
 *
 * Note the ordering: exposure outranks masking. The two are not mutually exclusive — a VPN
 * adapter, a Docker bridge or a host-only NIC yields an `.local` name AND a raw 192.168 address
 * in the same run — and the footer's if/else chain happens to show the raw one. That must be a
 * tested rule here, not a side effect of JSX order there.
 */
export function localNetworkNote(
  addresses: readonly { kind: AddressKind }[],
  permission: "granted" | "not-granted" | "unknown"
): LocalNetworkNote {
  // The browser could not answer the permission query. "We could not look" explains nothing.
  if (permission === "unknown") return null;
  if (maskableLocals(addresses) > 0) {
    return permission === "granted" ? "exposed-with-capture" : "exposed-otherwise";
  }
  // Masked AND granted is left silent: this origin never calls getUserMedia, so it is very
  // nearly unreachable, and "your browser is protecting you harder than it had to" is a shrug
  // rather than something a visitor can act on.
  if (permission === "not-granted" && addresses.some((a) => a.kind === "mdns")) {
    return "masked-unprompted";
  }
  return null;
}
